/**
 * neutralScores.ts
 *
 * Computes 7 dimension scores (0–1) from country metrics WITHOUT any quiz /
 * user context.  Used by the CountryHubPage snapshot radar to show an
 * objective profile of the country rather than a personalised match score.
 *
 * Normalization anchors are chosen to spread the 30-country dataset — not
 * tied to any single user's budget, passport, or priorities.
 */

import type { CountryMetrics } from '@/data/countries';
import { getHouseholdTotal, type CostOfLivingFile } from './cost-of-living';

export const NEUTRAL_DIMENSIONS = [
  'visa',
  'cost',
  'tax',
  'climate',
  'safety',
  'language',
  'family',
] as const;

export type NeutralDimension = (typeof NEUTRAL_DIMENSIONS)[number];

export type NeutralScores = Record<NeutralDimension, number>;

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

const BAND_SCORE: Record<string, number> = {
  very_high: 1.0,
  high: 0.85,
  moderate: 0.55,
  low: 0.3,
};

/**
 * Returns an objective 0–1 score for each of the 7 hub dimensions.
 *
 * Design principles:
 * - No user answers involved — every score reflects the country alone.
 * - Anchors are calibrated against the full 30-country dataset.
 * - Formula matches the corresponding quiz scoring function where possible
 *   so values stay comparable to personalised results.
 */
export function computeNeutralScores(
  metrics: CountryMetrics,
  costFile?: CostOfLivingFile,
  countryCode?: string,
): NeutralScores {
  // ── Visa ──────────────────────────────────────────────────────────────────
  // Start from 0.4; bonus for each available pathway; no passport dependency.
  let visa = 0.4;
  if (metrics.visa.digital_nomad) visa += 0.2;
  if (metrics.visa.freelance_path) visa += 0.15;
  if (metrics.visa.schengen) visa += 0.1;
  const ytc = metrics.visa.years_to_citizenship;
  if (ytc != null) {
    if (ytc <= 5) visa += 0.15;
    else if (ytc <= 10) visa += 0.05;
  }

  // ── Cost ──────────────────────────────────────────────────────────────────
  // 2026-08-18: this module deliberately carries no user context, so there's
  // no quiz-derived household bucket to key off. We use `family_2` (2 adults
  // + 2 kids) as the fixed reference household — the closest sourced
  // equivalent to the old field's implied "family of ~4" framing — read from
  // cost_of_living.json when it's loaded and covers this country. Falls back
  // to the legacy (fabricated) `monthly_eur_family` figure otherwise; same
  // null-means-fall-back contract as scoreCost() in scoring.ts.
  // Monthly cost range across the 30-country set: ~800 (GE/RS) → ~5000 (CH/SG).
  const householdTotal = countryCode
    ? getHouseholdTotal(costFile, countryCode, 'family_2')
    : null;
  const costFigure = householdTotal ?? metrics.cost.monthly_eur_family;
  const cost = clamp01(1 - (costFigure - 800) / 4200);

  // ── Tax ───────────────────────────────────────────────────────────────────
  // Base: linear on top PIT rate, ceiling 75 %.
  // Flat freelance regime (≤15 %) overrides — matches quiz scoreTax() fallback.
  let tax = clamp01(1 - metrics.tax.pit_top_rate_pct / 75);
  if (metrics.tax.flat_freelance_pct != null && metrics.tax.flat_freelance_pct <= 15) {
    tax = Math.max(tax, 0.85);
  }

  // ── Climate ───────────────────────────────────────────────────────────────
  // Objective quality: sunshine hours + mild winter.
  // sunshine_h range: ~1200 (FI/EE) → ~3500 (AE/TH).
  // jan_min_c: mild 0–15 °C is optimal for temperate preference.
  const sunScore = clamp01(metrics.climate.sunshine_h / 3500);
  const winterScore = clamp01((metrics.climate.jan_min_c + 10) / 25);
  const climate = sunScore * 0.55 + winterScore * 0.45;

  // ── Safety ────────────────────────────────────────────────────────────────
  // GPI: 1.0 (best) → 4.0 (worst).  Identical to quiz scoreSafety().
  const safety = clamp01(1 - (metrics.safety.gpi - 1.0) / 3.0);

  // ── Language ──────────────────────────────────────────────────────────────
  // English accessibility band.  Matches BAND_SCORE in quiz scoreLanguage().
  const language = BAND_SCORE[metrics.language.english_band] ?? 0.5;

  // ── Family ────────────────────────────────────────────────────────────────
  // school_quality (0–1) + small bonus for english-medium schools available.
  let family = clamp01(metrics.family.school_quality);
  if (metrics.family.english_schools) family = clamp01(family + 0.1);

  return {
    visa: clamp01(visa),
    cost,
    tax,
    climate,
    safety,
    language,
    family,
  };
}
